import React, { useState } from 'react';
import { ToleranceList } from './ToleranceList';
import { ToleranceForm } from './ToleranceForm';
import { BookIcon, ChevronRightIcon, ScaleIcon } from 'lucide-react';
import { RoundingRule } from '../rounding/RoundingConfiguration';
export type ToleranceScope = 'Global' | 'Vendor' | 'Entity' | 'Site';
export type ToleranceRule = {
  id: string;
  toleranceType: 'Amount' | 'Percentage' | 'Combined';
  value: number;
  percentageValue?: number;
  maxAmount?: number;
  roundingId: string | null;
  roundingName: string | null;
  scope: ToleranceScope;
  scopeId: string | null;
  scopeName: string | null;
  modules: string[]; // 'All', 'Invoice', 'PO', 'Budget', etc.
  active: boolean;
  createdAt: string;
  updatedAt: string;
};
export function ToleranceConfiguration({
  locale = 'en-US'
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<ToleranceRule | null>(null);
  const [toleranceRules, setToleranceRules] = useState<ToleranceRule[]>([{
    id: 'tol-001',
    toleranceType: 'Amount',
    value: 5.0,
    roundingId: 'round-001',
    roundingName: 'Standard Cents',
    scope: 'Global',
    scopeId: null,
    scopeName: null,
    modules: ['All'],
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'tol-002',
    toleranceType: 'Percentage',
    value: 2.0,
    roundingId: 'round-002',
    roundingName: 'Nickel Rounding',
    scope: 'Vendor',
    scopeId: 'vendor-001',
    scopeName: 'Office Depot',
    modules: ['Invoice', 'PO'],
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'tol-003',
    toleranceType: 'Amount',
    value: 10.0,
    roundingId: null,
    roundingName: null,
    scope: 'Entity',
    scopeId: 'entity-001',
    scopeName: 'Global Technologies Inc.',
    modules: ['Budget'],
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }, {
    id: 'tol-004',
    toleranceType: 'Percentage',
    value: 5.0,
    roundingId: 'round-003',
    roundingName: 'Whole Dollar',
    scope: 'Site',
    scopeId: 'site-001',
    scopeName: 'New York HQ',
    modules: ['Invoice'],
    active: false,
    createdAt: '2023-02-15T00:00:00Z',
    updatedAt: '2023-03-01T00:00:00Z'
  }, {
    id: 'tol-005',
    toleranceType: 'Combined',
    percentageValue: 3.0,
    maxAmount: 500.0,
    value: 0,
    roundingId: 'round-001',
    roundingName: 'Standard Cents',
    scope: 'Global',
    scopeId: null,
    scopeName: null,
    modules: ['Invoice', 'PO'],
    active: true,
    createdAt: '2023-03-10T00:00:00Z',
    updatedAt: '2023-03-10T00:00:00Z'
  }]);
  const [roundingRules, setRoundingRules] = useState<RoundingRule[]>([{
    id: 'round-001',
    name: 'Standard Cents',
    description: 'Round to nearest cent (0.01)',
    roundingType: 'Nearest',
    interval: 0.01,
    active: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'round-002',
    name: 'Nickel Rounding',
    description: 'Round up to nearest 0.05',
    roundingType: 'Up',
    interval: 0.05,
    active: true,
    createdAt: '2023-01-15T00:00:00Z',
    updatedAt: '2023-01-15T00:00:00Z'
  }, {
    id: 'round-003',
    name: 'Whole Dollar',
    description: 'Round down to nearest dollar',
    roundingType: 'Down',
    interval: 1.0,
    active: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }]);
  const handleAddRule = () => {
    setEditingRule(null);
    setShowForm(true);
  };
  const handleEditRule = (rule: ToleranceRule) => {
    setEditingRule(rule);
    setShowForm(true);
  };
  const handleSaveRule = (rule: Omit<ToleranceRule, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingRule) {
      // Update existing rule
      setToleranceRules(toleranceRules.map(r => r.id === editingRule.id ? {
        ...editingRule,
        ...rule,
        updatedAt: new Date().toISOString()
      } : r));
    } else {
      // Create new rule
      const newRule: ToleranceRule = {
        id: `tol-${String(toleranceRules.length + 1).padStart(3, '0')}`,
        ...rule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setToleranceRules([...toleranceRules, newRule]);
    }
    setShowForm(false);
    setEditingRule(null);
  };
  const handleToggleActive = (id: string) => {
    setToleranceRules(toleranceRules.map(rule => rule.id === id ? {
      ...rule,
      active: !rule.active,
      updatedAt: new Date().toISOString()
    } : rule));
  };
  const handleDeleteRule = (id: string) => {
    setToleranceRules(toleranceRules.filter(rule => rule.id !== id));
  };
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingRule(null);
  };
  return <div data-id="element-10010">
      <div className="mb-8" data-id="element-10011">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-10012">
          Tolerance Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-10013">
          Define how much deviation is acceptable when matching values (e.g.
          invoice vs PO)
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb" data-id="element-10014">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-10015">
          <li className="inline-flex items-center" data-id="element-10016">
            <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-10017">
              <BookIcon className="w-4 h-4 mr-2" data-id="element-10018" />
              Financial
            </span>
          </li>
          <li data-id="element-10019">
            <div className="flex items-center" data-id="element-10020">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-10021" />
              <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-10022">
                <ScaleIcon className="w-4 h-4 mr-2" data-id="element-10023" />
                Tolerance
              </span>
            </div>
          </li>
        </ol>
      </nav>
      {showForm ? <ToleranceForm rule={editingRule} roundingRules={roundingRules.filter(rule => rule.active)} onSave={handleSaveRule} onCancel={handleCancelForm} locale={locale} data-id="element-10024" /> : <ToleranceList rules={toleranceRules} onAdd={handleAddRule} onEdit={handleEditRule} onToggleActive={handleToggleActive} onDelete={handleDeleteRule} locale={locale} data-id="element-10025" />}
    </div>;
}